import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUsersStore } from '../store/useUsersStore';

const normalizeSkill = (s, idx) => {
  if (s === null || typeof s === 'undefined') return { id: `unknown-${idx}`, name: String(s ?? ''), template: '' };

  if (typeof s === 'object') {
    const rawId = s.id ?? (s.name ? String(s.name).trim() : `unknown-${idx}`);
    const id = typeof rawId === 'string' || typeof rawId === 'number' ? String(rawId) : `skill-${idx}`;
    return { id, name: s.name ?? s.skill ?? String(s), template: s.template ?? '' };
  }

  if (typeof s === 'string') {
    return { id: `${s}-${idx}`, name: s.trim(), template: '' };
  }

  return { id: `unknown-${idx}`, name: String(s), template: '' };
};

// Helper to dedupe by id preserving order
const dedupeById = (arr = []) => {
  const map = new Map();
  arr.forEach((it) => {
    const id = String(it?.id ?? '');
    if (!id) return;
    if (!map.has(id)) map.set(id, it);
  });
  return Array.from(map.values());
};

const Skills = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedUser = useUsersStore((s) => s.getSelectedUser());
  const updateSubUser = useUsersStore((s) => s.updateSubUser);
  const fetchUsers = useUsersStore((s) => s.fetchUsers);
  const parentUid = useUsersStore((s) => s.parentUid);

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const [included, setIncluded] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [tab, setTab] = useState('skills');
  const [searchIncluded, setSearchIncluded] = useState('');
  const [searchExcluded, setSearchExcluded] = useState('');
  const [loading, setLoading] = useState(false);
  const [showProjectsToggle, setShowProjectsToggle] = useState(false);

  // Sync local arrays when selectedUser changes
  useEffect(() => {
    if (!selectedUser) {
      setIncluded([]);
      setExcluded([]);
      setShowProjectsToggle(false);
      return;
    }

    const inc = (selectedUser.skills ?? []).map(normalizeSkill);
    const exc = (selectedUser.excludedSkills ?? selectedUser.excluded_skills ?? []).map(normalizeSkill);

    const excIds = new Set(exc.map((s) => s.id));
    const dedupedInc = dedupeById(inc.filter((s) => !excIds.has(s.id)));
    const dedupedExc = dedupeById(exc);

    setIncluded(dedupedInc);
    setExcluded(dedupedExc);
    setShowProjectsToggle(!!selectedUser.showExcludedOnSearch);
  }, [selectedUser]);

  // Handle back/forward navigation: refetch users if selectedUser is missing
  useEffect(() => {
    if (selectedUser) return;

    const load = async () => {
      try {
        if (parentUid) await fetchUsers(parentUid);
        else await fetchUsers();
      } catch (e) { /* ignore */ }
    };
    load();
  }, [selectedUser, parentUid, fetchUsers, location.key]);

  const filteredIncluded = useMemo(() => {
    const q = (searchIncluded || '').trim().toLowerCase();
    if (!q) return included;
    return included.filter((s) => s.name.toLowerCase().includes(q));
  }, [included, searchIncluded]);

  const filteredExcluded = useMemo(() => {
    const q = (searchExcluded || '').trim().toLowerCase();
    if (!q) return excluded;
    return excluded.filter((s) => s.name.toLowerCase().includes(q));
  }, [excluded, searchExcluded]);

  const persistChanges = useCallback(async (nextIncluded, nextExcluded, extra = {}) => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      const serialize = (s) => {
        const rawId = s?.id;
        const id = (typeof rawId === 'string' && /^\d+$/.test(rawId)) ? Number(rawId) : rawId;
        const safe = { id: typeof id !== 'undefined' ? id : undefined, name: s.name, template: s.template ?? '' };
        if (typeof safe.id === 'undefined') delete safe.id;
        return safe;
      };

      const body = {
        skills: (nextIncluded || []).map(serialize),
        excludedSkills: (nextExcluded || []).map(serialize),
        ...extra
      };

      await updateSubUser(selectedUser, body);
    } catch (err) {
      console.error('Persist skills error', err);
      alert('Failed to save skills: ' + (err?.message || String(err)));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [selectedUser, updateSubUser]);
  
const removeSkill = (skill) => {
  setIncluded((prev) => prev.filter((s) => s.id !== skill.id));
  setExcluded((prev) => [skill, ...prev]);
};

const restoreSkill = (skill) => {
  setExcluded((prev) => prev.filter((s) => s.id !== skill.id));
  setIncluded((prev) => [...prev, skill]);
};


  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    await persistChanges(included, excluded, { showExcludedOnSearch: !!showProjectsToggle });
  }, [included, excluded, persistChanges, showProjectsToggle]);

  const handleSync = useCallback(async () => {
    setLoading(true);
    try {
      await fetchUsers(parentUid || undefined);
    } catch (err) {
      console.error('Sync skills failed', err);
      alert('Sync failed: ' + (err?.message || String(err)));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [fetchUsers, parentUid]);

  return (
    <div className="max-w-[60rem] 2xl:max-w-[1066px] mx-auto px-4 font-sans">
      <header className="mb-6">
        <div className="flex items-center gap-4 mb-2">
          <button
            className="flex items-center justify-center"
            onClick={() => {
              try {
                if (window.history.length > 1) navigate(-1);
                else navigate('/dashboard', { replace: true });
              } catch {
                window.location.href = '/dashboard';
              }
            }}
          >
            <img src="/Icons/arrow-back.svg" alt="Back" className="w-8 h-8" />
          </button>
          <h1 className="text-3xl font-semibold text-gray-900">Skills</h1>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-white p-2 rounded-xl shadow-sm w-full border-2" style={{ borderColor: '#E6F0FF' }}>
        <button
          onClick={() => setTab('skills')}
          className={`px-6 py-2 rounded-lg font-medium transition-all w-1/2 ${tab === 'skills' ? 'bg-primary-blue text-white shadow-md' : 'text-primary-blue hover:bg-gray-50'}`}
          style={{ backgroundColor: tab === 'skills' ? '#1F4ECF' : '#E6F0FF', color: tab === 'skills' ? 'white' : '#1F4ECF' }}
        >Skills</button>
        <button
          onClick={() => setTab('skills-set')}
          className={`px-6 py-2 rounded-lg font-medium transition-all w-1/2 ${tab === 'skills-set' ? 'bg-primary-blue text-white shadow-md' : 'text-primary-blue hover:bg-gray-50'}`}
          style={{ backgroundColor: tab === 'skills-set' ? '#1F4ECF' : '#E6F0FF', color: tab === 'skills-set' ? 'white' : '#1F4ECF' }}
        >Skills Set</button>
      </div>

      <div className="bg-transparent mt-6">
        {tab === 'skills' && (
          <div className="space-y-6">
            {/* Included */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-transparent">
              <div className="flex items-start justify-between mb-4">
                <div className="max-w-[720px]">
                  <h2 className="text-lg font-semibold">Manage Included Skills</h2>
                  <p className="text-sm text-gray-500 mt-2 max-w-2xl">Notice! This section only shows skills that are added on your profile. If you don't see correct skills, please press Sync Skills button.</p>
                </div>
                <div>
                  <button onClick={handleSync} disabled={loading} className="px-4 py-2 rounded-md border-[1.5px] border-primary-blue bg-white text-primary-blue hover:bg-primary-blue hover:text-white transition-colors">
                    {loading ? 'Syncing...' : 'Sync Skills'}
                  </button>
                </div>
              </div>

              <div className="mb-4 relative">
                <input type="text" placeholder="Search here" value={searchIncluded} onChange={(e) => setSearchIncluded(e.target.value)}
                  className="w-full px-4 py-3 border-[1.5px] border-primary-blue focus:border-2 rounded-lg text-sm focus:outline-none"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60">
                  <img src="/Icons/search.svg" alt="Search" className="w-5 h-5" />
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                {filteredIncluded.length === 0 ? <div className="text-sm text-gray-500">No included skills.</div> :
                  filteredIncluded.map((s) => (
                    <div key={s.id} className="w-[175px] h-[40px] mx-auto flex items-center justify-between rounded-md px-3" style={{ background: '#E6F0FF', border: '1px solid #1F4ECF' }} role="button">
                      <span className="flex-1 text-center text-sm font-medium text-[#1F4ECF] truncate">{s.name}</span>
                      <button onClick={() => removeSkill(s)} disabled={loading} className="ml-2 flex-shrink-0 w-8 h-8 flex items-center justify-center rounded" aria-label={`Exclude ${s.name}`}>
                        <img src="/Icons/cross-icon.svg" alt="Remove" className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
              </div>

              <div className="mt-6 flex justify-center">
                <button onClick={handleSubmit} disabled={loading} className="rounded-md text-white bg-primary-blue px-16 py-2.5 hover:bg-primary-blue/60">
                  {loading ? 'Saving...' : 'Submit'}
                </button>
              </div>
            </div>

            {/* Excluded */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-transparent">
              <h2 className="text-lg font-semibold mb-2">Manage Excluded Skills</h2>
              <p className="text-sm text-gray-500 mb-4">Notice! These skills will always be ignored when auto bidding is enabled.</p>

              <div className="mb-4 border rounded p-3 flex items-center justify-between" style={{ borderColor: '#E6F0FF' }}>
                <div>
                  <div className="text-sm font-medium">Show project for these skills on search page?</div>
                  <div className="text-xs text-gray-400">(Fast searching when enabled)</div>
                </div>
                <label className="inline-flex items-center space-x-2">
                  <input type="checkbox" checked={showProjectsToggle} onChange={(e) => setShowProjectsToggle(e.target.checked)} className="form-checkbox h-5 w-9 border-[1.5px] border-primary-blue" />
                  <span className="text-sm">{showProjectsToggle ? 'On' : 'Off'}</span>
                </label>
              </div>

              <div className="mb-4">
                <input type="text" placeholder="Search here" value={searchExcluded} onChange={(e) => setSearchExcluded(e.target.value)} className="w-full px-4 py-3 border-[1.5px] border-primary-blue rounded-lg text-sm focus:outline-none focus:border-2" />
              </div>

              <div className="flex flex-wrap gap-3">
                {filteredExcluded.length === 0 ? <div className="text-sm text-gray-500">No excluded skills.</div> :
                  filteredExcluded.map((s) => (
                    <div key={s.id} className="flex items-center gap-3 rounded px-4 py-2" style={{ background: '#F7FAFF', border: '1px solid #E6F0FF' }}>
                      <span className="text-sm text-gray-700">{s.name}</span>
                      <button title="Include" onClick={() => restoreSkill(s)} disabled={loading} className="ml-2 rounded flex items-center justify-center" style={{ width: 28, height: 28 }}>
                        <img src="/Icons/cross-icon.svg" alt="Include" className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
              </div>

              <div className="mt-6 flex justify-end">
                <button onClick={handleSubmit} disabled={loading} className="px-6 py-2.5 rounded-md text-white bg-primary-blue hover:bg-primary-blue/60">
                  {loading ? 'Saving...' : 'Save Excluded'}
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === 'skills-set' && (
          <div className="mt-6">
            <div className="bg-white rounded shadow p-6">
              <h3 className="text-lg font-semibold">Skills Set</h3>
              <p className="text-sm text-gray-500">Manage named skill sets here (implement as needed).</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Skills;
